import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { ApiEnvelope, ApiErrorBody } from './types';

// axios 전역 기본값 — refresh_token HttpOnly 쿠키 자동 송수신 (권위: ARCH_DASHBOARD §6.0.3 / §6.0.4)
axios.defaults.withCredentials = true;

// ── ApiError ─────────────────────────────────────────
export class ApiError extends Error {
  code: string;
  httpStatus: number;
  /** envelope.meta — 일부 엔드포인트(예: bulk-merge 422)가 violation detail 을
   *  meta 로 함께 내려준다. ARCH_DASHBOARD §4.11 화면 11 toast 표시용. */
  meta: Record<string, unknown> | null;
  constructor(
    code: string,
    message: string,
    httpStatus: number,
    meta: Record<string, unknown> | null = null
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.meta = meta;
  }
}

// ── authStore 우회 의존 (순환 import 방지) ─────────
// 실제 주입은 main.tsx 시작 시 1회 수행된다.
type AuthHooks = {
  getAccessToken: () => string | null;
  setAccessToken: (token: string) => void;
  clear: () => void;
  redirectToLogin: (reason: 'unauthorized' | 'refresh_failed') => void;
};

let hooks: AuthHooks | null = null;
export function installAuthHooks(h: AuthHooks): void {
  hooks = h;
}

// ── axios instance ───────────────────────────────────
export const apiClient: AxiosInstance = axios.create({
  baseURL: '/',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// 요청 인터셉터 — Bearer 자동 첨부
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = hooks?.getAccessToken();
  if (token && config.headers) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ── Refresh 재시도 동시성 억제 ─────────────────────
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  try {
    // raw axios 호출 — interceptor 를 타지 않도록 (무한 루프 방지)
    const resp = await axios.post<ApiEnvelope<{ accessToken: string; tokenType: string; expiresIn: number }>>(
      '/api/v1/auth/refresh',
      {},
      { withCredentials: true }
    );
    const env = resp.data;
    if (!env?.success || !env.data?.accessToken) return null;
    const newToken = env.data.accessToken;
    hooks?.setAccessToken(newToken);
    return newToken;
  } catch {
    return null;
  }
}

// 응답 인터셉터 — envelope 언랩 + 401 refresh 재시도
apiClient.interceptors.response.use(
  (resp) => {
    // Spring Boot 컨트롤러는 대부분 { success, data, meta, error } 래퍼를 반환한다.
    // 일부 엔드포인트(/verify-email 302, /logout 204)는 래퍼 없음.
    const body = resp.data;
    if (body && typeof body === 'object' && 'success' in body) {
      const env = body as ApiEnvelope<unknown>;
      if (env.success === false) {
        const err = env.error ?? { code: 'unknown_error', message: 'unknown error' };
        return Promise.reject(new ApiError(err.code, err.message, resp.status));
      }
      // 언랩: data 를 resp.data 에 대체
      resp.data = env.data;
    }
    return resp;
  },
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    // refresh 엔드포인트 자체의 401 은 재시도 무의미 — 즉시 로그인 페이지로
    const url = original?.url ?? '';
    const isRefreshCall = url.includes('/api/v1/auth/refresh');
    const isLoginCall = url.includes('/api/v1/auth/login');

    if (status === 401 && original && !original._retried && !isRefreshCall && !isLoginCall) {
      original._retried = true;
      // 동시에 여러 요청이 401 로 깨질 수 있으므로 promise 공유
      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        if (original.headers) {
          original.headers.set('Authorization', `Bearer ${newToken}`);
        }
        return apiClient.request(original);
      }
      // refresh 실패 → 세션 종료 & 로그인 리다이렉트
      hooks?.clear();
      hooks?.redirectToLogin('refresh_failed');
      return Promise.reject(new ApiError('refresh_failed', 'session expired', 401));
    }

    // envelope 에 에러가 있는 경우 ApiError 로 변환
    const body = error.response?.data;
    if (body && typeof body === 'object' && 'success' in body) {
      const env = body as ApiEnvelope<unknown>;
      if (env?.success === false && env.error) {
        const e = env.error as ApiErrorBody;
        return Promise.reject(
          new ApiError(e.code, e.message, status ?? 0, env.meta ?? null)
        );
      }
    }
    return Promise.reject(
      new ApiError(
        error.code ?? 'network_error',
        error.message ?? 'network error',
        status ?? 0
      )
    );
  }
);

// ── 편의 헬퍼 ───────────────────────────────────────
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const resp = await apiClient.get<T>(url, config);
  return resp.data;
}

export async function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const resp = await apiClient.post<T>(url, body, config);
  return resp.data;
}

export async function apiPatch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const resp = await apiClient.patch<T>(url, body, config);
  return resp.data;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const resp = await apiClient.delete<T>(url, config);
  return resp.data;
}
